import React from 'react';
import { Alert } from 'antd';

/**
 * What a form's boxes cut (KPR-1861, KPE-041): one warning naming each block and how
 * much of it is hidden, in words. Shown on the screen only, in the designer over a
 * sample value and on the print route over the record's own, never on the paper
 * (the print route gives it `print-hide`).
 *
 * `cuts` is `useBlockFit`'s answer. A form of several pages names the page. A
 * repeating group's entries that had no line to go on are listed here too (KPR-1863).
 */

const SHOWN = 10;

export default function PageFitNotice({ cuts, pages = 1, sample = false, className = '' }) {
	if (!cuts?.length) return null;
	const many = cuts.length > 1;
	// A repeating group's rows are cells and entries, not blocks (KPR-1863).
	const lines = cuts.some(cut => cut.group);
	const blockMessage = sample
		? (many ? `Sample values do not fit the boxes of ${cuts.length} blocks.` : 'A sample value does not fit its box.')
		: (many ? `${cuts.length} blocks do not fit their boxes, and part of each is cut.` : '1 block does not fit its box, and part of it is cut.');
	const message = lines ? (sample ? 'Sample values do not fit every place.' : 'Part of this record does not fit its lines and boxes.') : blockMessage;
	const blockHelp = sample
		? 'Part of it would be cut on the paper. Make the box larger. A record may be longer or shorter than the sample: the print preview shows what a real one cuts.'
		: 'What is cut does not print. Make the box larger in the page layout to print all of it.';
	const help = lines && !sample ? 'What is cut or not shown does not print. Make the box or the area larger in the page layout to print more of it.' : blockHelp;
	return (
		<Alert
			className={`page-fit-notice ${className}`.trim()}
			type="warning"
			showIcon
			message={message}
			description={(
				<>
					<ul className="page-fit-notice__list">
						{cuts.slice(0, SHOWN).map(cut => (
							<li key={cut.key}>{`${cut.name}${pages > 1 ? `, page ${cut.page}` : ''}: ${cut.text}.`}</li>
						))}
						{cuts.length > SHOWN ? <li>{`and ${cuts.length - SHOWN} more.`}</li> : null}
					</ul>
					<p className="page-fit-notice__help">{help}</p>
				</>
			)}
		/>
	);
}
